/**
 * Default: Initial
 */

import {setRootVar} from '@ifabnpm/bootstrap/utils.js';
import {focusVisible} from '@ifabnpm/bootstrap/utils/focus-visible.js';
import {scrollTop} from '@ifabnpm/bootstrap/utils/scroll.js';
import {load} from '@ifabnpm/bootstrap/utils/load.js';
import lazyLoad from '@ifabnpm/bootstrap/utils/lazy-load.js';

import SelectorEngine from '@ifabnpm/bootstrap/dom/selector-engine.js';
import Manipulator from '@ifabnpm/bootstrap/dom/manipulator.js';
import EventHandler from '@ifabnpm/bootstrap/dom/event-handler.js';

import MindshapeCookieConsent from '../modules/mindshape-cookie-consent';

/**
 * Monitor ´document.documentElement´ changes.
 *
 * @type {MutationObserver}
 */
const observerDocumentMutation = new MutationObserver((mutations) => {
	EventHandler.trigger(document.documentElement, 'ifab.documentMutation', {
		mutations
	});
});

const observerDocHeight = () => {
	const calc = () => {
		setRootVar(`${window.pageInstance.prefix}vh`, `${window.innerHeight/100}px`);
	};

	window.addEventListener('resize', calc);
	window.addEventListener('orientationchange', calc);

	calc();
};

/**
 * Accessibility (`:focus` erst bei Tastaturnavigation anzeigen.)
 */
const handleFocusableElements = () => {
	const collection = SelectorEngine.find('input, textarea, select, label, a, button');

	for (const element of collection) {
		focusVisible.observe(element);
	}
};

const bindLazyload = () => {
	// Fehlerbehandlung von ´fehlenden Bildern´ anbinden.
	const images = SelectorEngine.find('img');

	for (const image of images) {
		EventHandler.on(image, 'error', (event) => {
			Manipulator.addClass(event.target, 'is-faulty');
		});
	}

	// Lazyload initialisieren.
	lazyLoad(function () {
		const lazyElements = SelectorEngine.find('.lazyload[data-src]');

		if (lazyElements) {
			window.lazySizesConfig = window.lazySizesConfig || {};
			window.lazySizesConfig.loadMode = 1;

			load(`${window.pageInstance.path.dist}vendors/lazysizes/lazysizes.min.js`).then(() => {
				// Do nothing...
			});
		}
	});
};

/**
 * E-Mail-Spam-Schutz: (at) und (dot) in @ und . umwandeln und href bei data-mailto-token auflösen.
 */
const handleEmailProtection = () => {
	const decodeText = (text) => {
		return text.replace(/\(at\)/g, '@').replace(/\(dot\)/g, '.');
	};

	const decryptToken = (token, vector) => {
		const encryptCharcode = (n, start, end, offset) => {
			n += offset;
			if (offset > 0 && n > end) {
				n = start + (n - end - 1);
			} else if (offset < 0 && n < start) {
				n = end - (start - n - 1);
			}
			return String.fromCharCode(n);
		};

		let decrypted = '';
		const offset = -vector;

		for (let i = 0; i < token.length; i++) {
			const charCode = token.charCodeAt(i);
			if (charCode >= 43 && charCode <= 58) {
				decrypted += encryptCharcode(charCode, 43, 58, offset);
			} else if (charCode >= 64 && charCode <= 90) {
				decrypted += encryptCharcode(charCode, 64, 90, offset);
			} else if (charCode >= 97 && charCode <= 122) {
				decrypted += encryptCharcode(charCode, 97, 122, offset);
			} else {
				decrypted += token.charAt(i);
			}
		}
		return decrypted;
	};

	const processLinks = () => {
		const links = SelectorEngine.find('a[href^="javascript:linkTo_UnConfidential"], a[data-mailto-token]');

		for (const link of links) {
			// Link-Text umwandeln
			const text = link.textContent;
			if (text.includes('(at)') || text.includes('(dot)')) {
				link.textContent = decodeText(text);
			}

			// href bei data-mailto-token auflösen
			const token = Manipulator.getDataAttribute(link, 'mailto-token');
			const vector = parseInt(Manipulator.getDataAttribute(link, 'mailto-vector'), 10);

			if (token && !isNaN(vector) && (link.getAttribute('href') === '#' || link.getAttribute('href') === '')) {
				const decrypted = decryptToken(token, vector);
				link.setAttribute('href', decrypted);
			}
		}
	};

	processLinks();

	// Falls Inhalte dynamisch nachgeladen werden
	EventHandler.on(document.documentElement, 'ifab.documentMutation', () => {
		processLinks();
	});
};

// -------
// Public
// -------

const index = () => {
	observerDocumentMutation.observe(document.documentElement, {
		attributes: true
	});

	observerDocHeight();
	bindLazyload();
	handleEmailProtection();
	handleFocusableElements();

	// ´History back´ - Buttons
	EventHandler.on(document.body, 'click', 'button[data-history-back]', (event) => {
		event.preventDefault();
		event.stopPropagation();

		history.back();
	});

	// ´Scroll top´ - Elwmente
	EventHandler.on(document, 'click', '[data-scroll-top]', (event) => {
		// preventDefault only for <a> elements (which change the URL) not inside the collapsible element
		if (
			event.target.tagName.toLowerCase() === 'a' ||
			(event.delegateTarget && event.delegateTarget.tagName.toLowerCase() === 'a')
		) {
			event.preventDefault();
		}

		scrollTop().then(element => {
			// `element` = zu ihm wird gescrollt
			// Do nothing ...
		});
	});

	// ...globalen ´Scroll top´-Button integrieren
	const scrollTopTrigger = SelectorEngine.findOne('button[data-scroll-top="global"]');

	if (scrollTopTrigger) {
		document.addEventListener('scroll', function () {
			requestAnimationFrame(() => {
				if (window.scrollY > window.innerHeight) {
					Manipulator.addClass(scrollTopTrigger, '-show');
				} else {
					Manipulator.removeClass(scrollTopTrigger, '-show');
				}
			});
		});
	}

	MindshapeCookieConsent.init();
};

// Export
export default index;
